import {afterNextRender, afterRenderEffect, Component, inject, OnInit, PLATFORM_ID, Renderer2, effect} from '@angular/core';
import {NavigationEnd, NavigationStart, PRIMARY_OUTLET, Router, RouterOutlet} from '@angular/router';
import {HeaderComponent} from "./ui/components/commons/header/header.component";
import {MenuSidebarComponent} from "./ui/components/commons/menu-sidebar/menu-sidebar.component";
import {ToastModule} from "primeng/toast";
import {ConfirmDialogModule} from "primeng/confirmdialog";
import {primengTranslations} from "./core/translations/primeng";
import {filter} from "rxjs";
import {LayoutService} from "./core/services/commons/layout.service";
import {NavigationService} from "./core/services/commons/navigation.service";
import {PrimeNG} from "primeng/config";
import Aura from '@primeng/themes/aura';
import {ScrollService} from "./core/services/commons/scroll.service";
import {PlatformService} from "./core/services/commons/platform.service";
import {isPlatformBrowser} from "@angular/common";
import {HeaderService} from "./core/services/commons/header.service";
import {CanonicalService} from "./core/services/commons/canonical.service";
import {ExternalResourcesService} from "./core/services/commons/external-resources.service";
import {MsalService} from "@azure/msal-angular";
import {ButtonComponent} from "./ui/components/commons/button/button.component";
import {ConfirmationService} from "primeng/api";
import {toSignal} from '@angular/core/rxjs-interop';
import {environment} from "../environments/environment";


@Component({
  selector: 'app-root',
  imports: [RouterOutlet, HeaderComponent, MenuSidebarComponent, ToastModule, ConfirmDialogModule, ButtonComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly primeNG = inject(PrimeNG)
  private readonly router = inject(Router)
  private readonly navigationService = inject(NavigationService)
  private readonly scrollService = inject(ScrollService)
  private readonly platformService = inject(PlatformService)
  private readonly canonicalService = inject(CanonicalService)
  private readonly externalResourcesService = inject(ExternalResourcesService)
  private readonly renderer = inject(Renderer2)
  private readonly msalService = inject(MsalService)
  protected readonly confirmationService = inject(ConfirmationService)
  protected readonly layoutService = inject(LayoutService)
  protected readonly headerService = inject(HeaderService)

  title = 'Alex';

  private navigationStartEvents = toSignal(
    this.router.events.pipe(filter(event => event instanceof NavigationStart)),
    { initialValue: null }
  );

  private navigationEndEvents = toSignal(
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
    { initialValue: null }
  );

  constructor() {
    this.platformService.isBrowser.update(()=>isPlatformBrowser(this.platformId));
    afterNextRender(() => {
      this.platformService.isNextRendered.update(()=>true);
    });
    afterRenderEffect(() => {
      this.platformService.isRendered.update(()=>true);
    });
    this.primeNG.theme.set({
      preset: Aura,
      options: {
        cssLayer: {
          name: 'primeng',
          order: 'tailwind-base, primeng, tailwind-utilities',
        },
        darkModeSelector: '.my-app-dark',
      },
    });
    this.primeNG.setTranslation(primengTranslations.fr);

    effect(() => {
      const navigationStart = this.navigationStartEvents();
      if (navigationStart) {
        this.navigationService.currentName.update(() => '');
        this.handleRouteChange(navigationStart);
      }
    });

    effect(() => {
      const navigationEnd = this.navigationEndEvents();
      if (navigationEnd) {
        this.scrollService.toTop(0);
        this.headerService.showMenu.update(() => false);
        this.canonicalService.setCanonical();
      }
    });
  }

  private handleRouteChange(event: NavigationStart) {
    this.layoutService.showPublicBurger.update(() => false);
    // Le layout se decide sur le premier segment de l'URL parsee : NavigationStart
    // porte l'URL demandee (query string et fragment compris) avant resolution des
    // redirectTo, qui n'emettent pas de second evenement.
    const rootSegment = this.router.parseUrl(event.url).root.children[PRIMARY_OUTLET]?.segments[0]?.path ?? '';
    this.layoutService.currentLayout.set(rootSegment === '' || rootSegment === 'admin' ? 'admin' : 'form');
  }

  lazyLoadDepencies() {
    if (this.platformService.isBrowser()) {

      if (this.router.url.includes('/admin')) {
        this.msalService.instance.initialize().then(() => {

        });
      } else {
        this.externalResourcesService.loadScript(this.renderer, {src: 'https://cache.consentframework.com/js/pa/45431/c/fBcbb/stub'});
        this.externalResourcesService.loadScript(this.renderer, {
          src: 'https://choices.consentframework.com/js/pa/45431/c/fBcbb/cmp',
          isAsync: true
        });
        this.externalResourcesService.loadScript(this.renderer, {
          isNotExterneal: true,
          text: '(function (c, l, a, r, i, t, y) {c[a] = c[a] || function () {(c[a].q = c[a].q || []).push(arguments)};t = l.createElement(r);t.async = 1;t.src = "https://www.clarity.ms/tag/" + i;y = l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t, y);})(window, document, "clarity", "script", "pr4mrvpb5p");'
        });
      }
    }
  }

  private async loadNewRelicScript(): Promise<void> {
    if (this.platformService.isBrowser()) {
      const newrelicAccountID: string = environment.newrelicAccountID;
      const newrelicTrustKey: string = environment.newrelicTrustKey;
      const newrelicAgentID: string = environment.newrelicAgentID;
      const newrelicLicenseKey: string = environment.newrelicLicenseKey;
      const newrelicApplicationID: string = environment.newrelicApplicationID;

      if (
        !newrelicAccountID
        || newrelicAccountID.length == 0
        || !newrelicTrustKey
        || newrelicTrustKey.length == 0
        || !newrelicAgentID
        || newrelicAgentID.length == 0
        || !newrelicLicenseKey
        || newrelicLicenseKey.length == 0
        || !newrelicApplicationID
        || newrelicApplicationID.length == 0
      ) return;

      const options = {
        init: {
          session_replay: {
            enabled: true,
            block_selector: '',
            mask_text_selector: '*',
            sampling_rate: 10.0,
            error_sampling_rate: 100.0,
            mask_all_inputs: true,
            collect_fonts: true,
            inline_images: false,
            inline_stylesheet: true,
            fix_stylesheets: true,
            preload: false,
            mask_input_options: {}
          },
          distributed_tracing: {
            enabled: true
          },
          privacy: {
            cookies_enabled: true
          },
          ajax: {
            deny_list: ["bam.eu01.nr-data.net"]
          }
        },
        info: {
          beacon: "bam.eu01.nr-data.net",
          errorBeacon: "bam.eu01.nr-data.net",
          licenseKey: newrelicLicenseKey,
          applicationID: newrelicApplicationID,
          sa: 1
        },
        loader_config: {
          accountID: newrelicAccountID,
          trustKey: newrelicTrustKey,
          agentID: newrelicAgentID,
          licenseKey: newrelicLicenseKey,
          applicationID: newrelicApplicationID
        }
      }

      const { BrowserAgent } = await import('@newrelic/browser-agent/loaders/browser-agent');
      new BrowserAgent(options);
    }
  }

  async ngOnInit() {
    await this.loadNewRelicScript();
    this.lazyLoadDepencies();
  }
}
